import ReactMarkdown from "react-markdown";
import { useCallback, useEffect, useState } from "react";

interface TypewriterTextProps {
  speed?: number;
  startDelay?: number;
  text: string;
  onComplete?: () => void;
  onProgress?: () => void;
}

export default function TypewriterText({
  onComplete,
  onProgress,
  speed = 50,
  startDelay = 200,
  text,
}: TypewriterTextProps) {
  const [displayedText, setDisplayedText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const startTyping = useCallback(() => {
    setHasStarted(true);
  }, []);

  useEffect(() => {
    if (startDelay > 0) {
      const delayTimeout = setTimeout(startTyping, startDelay);
      return () => clearTimeout(delayTimeout);
    } else {
      startTyping();
    }
  }, [startDelay, startTyping]);

  useEffect(() => {
    if (!hasStarted) return;

    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
        if (onProgress) {
          onProgress();
        }
      }, speed);

      return () => clearTimeout(timeout);
    } else if (!isComplete) {
      setIsComplete(true);
      if (onComplete) {
        onComplete();
      }
    }
  }, [
    currentIndex,
    text,
    speed,
    onComplete,
    isComplete,
    hasStarted,
    onProgress,
  ]);

  // Reset when text changes
  useEffect(() => {
    setDisplayedText("");
    setCurrentIndex(0);
    setIsComplete(false);
    setHasStarted(false);
  }, [text]);

  return (
    <div className="relative">
      <div className="inline-block">
        <ReactMarkdown>{displayedText}</ReactMarkdown>
        {hasStarted && !isComplete && (
          <span
            className=""
            style={{
              animation: "typewriter-blink 1s infinite",
            }}
          ></span>
        )}
      </div>
      <style>{`
        @keyframes typewriter-blink {
          0%, 50% {
            opacity: 1;
          }
          51%, 100% {
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
